/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";
import { assetImages } from "constants";
import PatientDetailsCard from "../PatientDetailsCard/PatientDetailsCard";

const SaveHeadCircumferencePopup = ({
  patientObj = null,
  afterActionClose = () => {},
  selectedHeadCircumferenceId = null, // Default value for selectedHeadCircumferenceId
  setSelectedHeadCircumferenceId = () => {},
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  // States for input fields
  const [circumference, setCircumference] = useState("");
  const [age, setAge] = useState(""); // New state for age
  const [circumferenceDate, setCircumferenceDate] = useState(
    new Date().toISOString().split("T")[0]
  ); // Default to today's date
  const [assignTo, setAssignTo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] =
    useState(false); /* New state for loading head circumference details */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!circumference) {
      newErrors.circumference = t("Please enter circumference value!");
    } else if (isNaN(circumference) || circumference <= 0) {
      newErrors.circumference = t("Please enter a valid circumference number!");
    }
    if (!circumferenceDate) {
      // Validation for new date field
      newErrors.circumferenceDate = t("Please enter a date!");
    }
    if (age && (isNaN(age) || age < 0 || age > 120)) {
      newErrors.age = t("Please enter a valid age between 0 and 120 years!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options for Assign To (from AddAllergyModal)
  const loadAssineeOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_USER_LIST_BY_USER +
        `?token=${token}&userstringinput=${inputValue}&modulefrom=backend&filterroleslug=PATIENT`;

      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  //function for get head circumference details
  const getHeadCircumferenceDetails = async () => {
    try {
      setIsLoading(true); /* Set loading to true before API call */
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_HEAD_CIRCUMFERENCE_BY_ID +
        `/${selectedHeadCircumferenceId}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const headCircumferenceData = response.data;

        setCircumference(headCircumferenceData.headcircumference);
        setAge(headCircumferenceData.age || "");
        setCircumferenceDate(
          headCircumferenceData.date
            ? new Date(headCircumferenceData.date).toISOString().split("T")[0]
            : new Date().toISOString().split("T")[0]
        ); // Set date from API data, default to today's date if no date is available
        setAssignTo(headCircumferenceData?.uservalue);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(
        false
      ); /* Set loading to false after API call (success or failure) */
    }
  };

  const saveHeadCircumferenceHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    try {
      setIsSaving(true);
      const apiData = {
        headcircumference: parseFloat(circumference),
        age: age ? parseFloat(age) : null,
        date: circumferenceDate, // Include date in API data
        user: assignTo ? assignTo?.value : null,
      };

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (selectedHeadCircumferenceId) {
        requestUrl +=
          url.API_UPDATE_HEAD_CIRCUMFERENCE +
          `/${selectedHeadCircumferenceId}?token=${token}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_CREATE_HEAD_CIRCUMFERENCE + `?token=${token}`;
        response = await postData(requestUrl, apiData);
      }

      setIsSaving(false);

      if (response.status) {
        resetHandler();
        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#saveHeadCircumferenceOffCanvas")
        ).hide();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.error("Error saving head circumference:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setCircumference("");
    setAge("");
    setCircumferenceDate(new Date().toISOString().split("T")[0]); // Reset to today's date
    setAssignTo(patientObj);
    setErrors({});
    setSelectedHeadCircumferenceId(null);
  };

  useEffect(() => {
    if (patientObj && !selectedHeadCircumferenceId) {
      setAssignTo(patientObj);
    }
  }, [patientObj, selectedHeadCircumferenceId]);

  useEffect(() => {
    if (selectedHeadCircumferenceId) {
      getHeadCircumferenceDetails();
    }
  }, [selectedHeadCircumferenceId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveHeadCircumferenceOffCanvas"
      aria-labelledby="saveHeadCircumferenceOffCanvas"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-5 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              circle
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="saveHeadCircumferenceOffCanvas"
            >
              {selectedHeadCircumferenceId
                ? t("Edit Head Circumference")
                : t("Save Head Circumference")}
            </h3>
            <p className="text-white fs-sm mb-0">
              {selectedHeadCircumferenceId
                ? t("Manage existing head circumference details")
                : t("Enter and save head circumference measurements")}
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100 bg-white bg-opacity-75 z-index-1050">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">
                {t("Loading head circumference details...")}
              </span>
            </div>
            <p className="text-primary mt-3 fw-bold">
              {t("Loading head circumference details...")}
            </p>
          </div>
        ) : null}

        {!isLoading && (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            <form
              onSubmit={(e) => e.preventDefault()}
              className="d-flex flex-column h-100"
            >
              <div className="fields-container flex-fill">
                {/* Circumference and Age */}
                <div className="row">
                  <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="circumference"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Head Circumference (cm) *
                      </label>
                      <input
                        type="number"
                        id="circumference"
                        className={`form-control fs-sm shadow-none ${
                          errors.circumference
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        placeholder="e.g., 55"
                        value={circumference}
                        onChange={(e) => {
                          setCircumference(e.target.value);
                          if (errors.circumference) {
                            setErrors((prev) => ({
                              ...prev,
                              circumference: "",
                            }));
                          }
                        }}
                      />
                      {errors.circumference && (
                        <div className="invalid-feedback">
                          {errors.circumference}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="age"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Age (years)
                      </label>
                      <input
                        type="number"
                        id="age"
                        className={`form-control fs-sm shadow-none ${
                          errors.age ? "is-invalid" : "border border-gray-300"
                        }`}
                        placeholder="e.g., 2"
                        value={age}
                        onChange={(e) => {
                          setAge(e.target.value);
                          if (errors.age) {
                            setErrors((prev) => ({
                              ...prev,
                              age: "",
                            }));
                          }
                        }}
                      />
                      {errors.age && (
                        <div className="invalid-feedback">{errors.age}</div>
                      )}
                    </div>
                  </div>
                  {/* Date input */}
                  <div className="col-lg-4 mb-3 mb-sm-4 mb-lg-0">
                    <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="circumferenceDate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Date *
                      </label>
                      <input
                        type="date"
                        id="circumferenceDate"
                        className={`form-control fs-sm shadow-none ${
                          errors.circumferenceDate
                            ? "is-invalid"
                            : "border border-gray-300"
                        }`}
                        value={circumferenceDate}
                        onChange={(e) => {
                          setCircumferenceDate(e.target.value);
                          if (errors.circumferenceDate) {
                            setErrors((prev) => ({
                              ...prev,
                              circumferenceDate: "",
                            }));
                          }
                        }}
                      />
                      {errors.circumferenceDate && (
                        <div className="invalid-feedback">
                          {errors.circumferenceDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assign To field */}
                <div className="form-group mb-3 mb-sm-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Assign To
                  </label>
                  <AsyncSelect
                    isClearable
                    name="assignTo"
                    loadOptions={loadAssineeOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    placeholder={t("Select Assignee")}
                    styles={getCustomStyles()}
                    value={assignTo}
                    onChange={setAssignTo}
                  />
                  {assignTo ? <PatientDetailsCard patient={assignTo} /> : null}
                </div>
              </div>

              {/* Head Circumference Interpretation Info */}
              <div className="p-4 border rounded-10 shadow-sm bg-gradient-light position-relative overflow-hidden">
                <div className="row align-items-center mb-3">
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <span className="material-symbols-outlined text-primary">
                        assessment
                      </span>
                      Head Circumference Guidelines
                    </h5>
                    <p className="text-muted fs-sm mb-0">
                      Measurement standards for pediatric assessment
                    </p>
                  </div>
                </div>

                <div className="interpretation-grid">
                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Measurement Tips
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Infants:</strong> Measure around the widest part
                      </span>
                      <span>
                        <strong>Children:</strong> Above eyebrows and ears
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-top border-gray-200">
                  <p className="text-muted fs-xs mb-0 d-flex align-items-center gap-1">
                    <span className="material-symbols-outlined fs-6">info</span>
                    Head circumference is crucial for monitoring brain growth
                    and development in infants and children.
                  </p>
                </div>
              </div>

              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveHeadCircumferenceHandler}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  disabled={isSaving ? true : false}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>{" "}
                      {t("Save Head Circumference")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveHeadCircumferencePopup;
